import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";

const AuthCtx = createContext(null);
const OFFLINE_USER = "mm_offline_user";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem(OFFLINE_USER, JSON.stringify({ id: data.id, name: data.name, has_paid: data.has_paid }));
      return data;
    } catch (e) {
      // No internet: keep the app (and its Safety page) usable from the last signed-in snapshot
      const snap = !e?.response && localStorage.getItem(OFFLINE_USER);
      if (snap) { const u = { ...JSON.parse(snap), offline: true }; setUser(u); return u; }
      if (e?.response?.status !== 401) logError("Failed to load session:", e);
      if (e?.response?.status === 401) ["mm_safety_plan_cache", OFFLINE_USER].forEach((k) => localStorage.removeItem(k));
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    localStorage.removeItem("mindful_me_token");
    // Returning from Google OAuth: AuthCallback exchanges session_id first
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  const afterAuth = useCallback(async () => {
    try {
      await api.post("/payments/claim", {});
    } catch (e) {
      logError("Payment claim failed:", e);
    }
    try {
      await api.post("/payment/sync-status");
    } catch (e) {
      logError("Payment sync failed:", e);
    }
    return refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (email, password) => {
    await api.post("/auth/login", { email, password });
    return afterAuth();
  }, [afterAuth]);

  const register = useCallback(async (name, email, password) => {
    await api.post("/auth/register", { name, email, password });
    return afterAuth();
  }, [afterAuth]);

  const googleExchange = useCallback(async (sessionId) => {
    await api.post("/auth/session", { session_id: sessionId });
    return afterAuth();
  }, [afterAuth]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      logError("Logout request failed:", e);
    }
    ["mm_safety_plan_cache", "mm_crisis_pin", "mm_crisis_pin_line", OFFLINE_USER].forEach((k) => localStorage.removeItem(k));
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, googleExchange, logout, refreshUser }),
    [user, loading, login, register, googleExchange, logout, refreshUser],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
